import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useEventsForPicker, useSessionsForPicker } from '../hooks/useVoting';
import type { AttendanceRule, Election, ThresholdBase, VoterKinds } from '../types';

export interface ElectionFormValues {
  name: string;
  description: string | null;
  event_id: string | null;
  session_id: string | null;
  attendance_rule: AttendanceRule;
  voter_kinds: VoterKinds;
  guests_enabled_default: boolean;
  threshold_pct: number;
  threshold_base: ThresholdBase;
  allow_blank: boolean;
  duration_minutes: number | null;
}

const NONE = '__none__';

interface ElectionFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  election?: Election | null;
  busy: boolean;
  onSubmit: (values: ElectionFormValues) => void;
}

export function ElectionFormDialog({ open, onOpenChange, election, busy, onSubmit }: ElectionFormDialogProps) {
  const [v, setV] = useState<ElectionFormValues>(defaults(election));
  const [pctText, setPctText] = useState(String(v.threshold_pct));
  const [durationText, setDurationText] = useState(v.duration_minutes ? String(v.duration_minutes) : '');
  const { data: events = [] } = useEventsForPicker();
  const { data: sessions = [] } = useSessionsForPicker(v.event_id);

  useEffect(() => {
    if (!open) return;
    const next = defaults(election);
    setV(next);
    setPctText(String(next.threshold_pct));
    setDurationText(next.duration_minutes ? String(next.duration_minutes) : '');
  }, [open, election]);

  const set = <K extends keyof ElectionFormValues>(k: K, val: ElectionFormValues[K]) => setV((p) => ({ ...p, [k]: val }));
  const pct = Number(pctText.replace(',', '.'));
  const duration = durationText.trim() === '' ? null : Number(durationText);
  const hasEvent = !!v.event_id;
  const errors = {
    name: v.name.trim().length < 3 ? 'Escribe un nombre (mínimo 3 letras).' : null,
    pct: !(pct > 0 && pct <= 100) ? 'Entre 1 y 100.' : null,
    duration: duration !== null && !(Number.isInteger(duration) && duration > 0) ? 'Minutos enteros, o vacío.' : null,
    session: v.attendance_rule === 'SESSION' && !v.session_id ? 'Elige la sesión.' : null,
  };
  const invalid = Object.values(errors).some(Boolean);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{election ? 'Configuración de la elección' : 'Nueva elección'}</DialogTitle>
          <DialogDescription>
            Cada ronda copia estos valores al crearse y puede cambiarlos antes de abrirse.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="el-name">Nombre</Label>
            <Input id="el-name" value={v.name} onChange={(e) => set('name', e.target.value)} placeholder="Ej. Directiva Juvenil 2026" />
            {errors.name && v.name !== '' && <p className="text-xs text-destructive">{errors.name}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="el-desc">Descripción (opcional)</Label>
            <Textarea id="el-desc" rows={2} value={v.description ?? ''} onChange={(e) => set('description', e.target.value || null)} />
          </div>

          <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">Quién vota</p>
            <div className="space-y-1.5">
              <Label>Evento (opcional)</Label>
              <Select
                value={v.event_id ?? NONE}
                onValueChange={(id) =>
                  setV((p) => ({
                    ...p,
                    event_id: id === NONE ? null : id,
                    session_id: null,
                    attendance_rule: id === NONE ? 'NONE' : p.attendance_rule,
                    voter_kinds: id === NONE ? 'MINISTERS' : p.voter_kinds,
                  }))
                }
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Sin evento (lista manual de votantes)</SelectItem>
                  {events.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Con evento, votan sus participantes. Sin evento, tú registras a los votantes (nombre y CI).
              </p>
            </div>

            {hasEvent && (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Participantes que votan</Label>
                    <Select value={v.voter_kinds} onValueChange={(x) => set('voter_kinds', x as VoterKinds)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MINISTERS">Solo ministros</SelectItem>
                        <SelectItem value="GUESTS">Solo invitados y delegados</SelectItem>
                        <SelectItem value="BOTH">Ministros, invitados y delegados</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Asistencia requerida</Label>
                    <Select value={v.attendance_rule} onValueChange={(x) => set('attendance_rule', x as AttendanceRule)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NONE">No se exige</SelectItem>
                        <SelectItem value="EVENT">Asistencia al evento</SelectItem>
                        <SelectItem value="SESSION">Asistencia a una sesión</SelectItem>
                        <SelectItem value="CHECKIN">Registro previo a la votación (escaneo de credencial)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {v.attendance_rule === 'CHECKIN' && (
                  <p className="text-sm text-muted-foreground">
                    Antes de votar, los ujieres escanean la credencial de cada votante. La asistencia al evento no basta. El registro se cierra al abrir la primera ronda.
                  </p>
                )}
                {v.attendance_rule === 'SESSION' && (
                  <div className="space-y-1.5">
                    <Label>Sesión de votación</Label>
                    <Select value={v.session_id ?? ''} onValueChange={(x) => set('session_id', x)}>
                      <SelectTrigger><SelectValue placeholder="Elige la sesión" /></SelectTrigger>
                      <SelectContent>
                        {sessions.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name} · {s.session_date}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.session && <p className="text-xs text-destructive">{errors.session}</p>}
                  </div>
                )}
                {v.voter_kinds !== 'MINISTERS' && (
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <Label htmlFor="el-guests">Invitados habilitados por defecto</Label>
                      <p className="text-xs text-muted-foreground">
                        Si está apagado, cada invitado debe habilitarse para votar en el listado de participantes (los delegados no lo necesitan).
                      </p>
                    </div>
                    <Switch id="el-guests" checked={v.guests_enabled_default} onCheckedChange={(x) => set('guests_enabled_default', x)} />
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Los delegados votan cuando la opción incluye invitados, y quedan habilitados por defecto.
                </p>
              </>
            )}
          </div>

          <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">Reglas de cada ronda</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="el-pct">Porcentaje para ganar</Label>
                <div className="flex items-center gap-2">
                  <Input id="el-pct" inputMode="decimal" className="w-24" value={pctText} onChange={(e) => setPctText(e.target.value)} />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
                {errors.pct && <p className="text-xs text-destructive">{errors.pct}</p>}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="el-dur">Duración (minutos, opcional)</Label>
                <Input id="el-dur" inputMode="numeric" className="w-28" value={durationText} onChange={(e) => setDurationText(e.target.value)} placeholder="Sin límite" />
                {errors.duration && <p className="text-xs text-destructive">{errors.duration}</p>}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>El porcentaje se calcula sobre</Label>
              <Select value={v.threshold_base} onValueChange={(x) => set('threshold_base', x as ThresholdBase)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ELIGIBLE">Habilitados para votar (quien no vota cuenta como blanco)</SelectItem>
                  <SelectItem value="VALID">Votos válidos (sin los blancos)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="el-blank">Mostrar la opción "Voto en blanco"</Label>
              <Switch id="el-blank" checked={v.allow_blank} onCheckedChange={(x) => set('allow_blank', x)} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            disabled={busy || invalid}
            onClick={() =>
              onSubmit({
                ...v,
                name: v.name.trim(),
                session_id: v.attendance_rule === 'SESSION' ? v.session_id : null,
                threshold_pct: pct,
                duration_minutes: duration,
              })
            }
          >
            {busy && <Loader2 className="mr-2 size-4 animate-spin" />}
            {election ? 'Guardar' : 'Crear elección'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function defaults(e?: Election | null): ElectionFormValues {
  return {
    name: e?.name ?? '',
    description: e?.description ?? null,
    event_id: e?.event_id ?? null,
    session_id: e?.session_id ?? null,
    attendance_rule: (e?.attendance_rule as AttendanceRule) ?? 'NONE',
    voter_kinds: (e?.voter_kinds as VoterKinds) ?? 'MINISTERS',
    guests_enabled_default: e?.guests_enabled_default ?? false,
    threshold_pct: e ? Number(e.threshold_pct) : 60,
    threshold_base: (e?.threshold_base as ThresholdBase) ?? 'ELIGIBLE',
    allow_blank: e?.allow_blank ?? false,
    duration_minutes: e?.duration_minutes ?? null,
  };
}
